import { useId } from 'react'
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, YAxis } from 'recharts'
import { useChartTheme } from './chartTheme'

interface GradientSparklineProps {
  values: number[]
  color: string
  /** Drawn as a dashed level, e.g. a position's entry price; kept in view. */
  baseline?: number
  label: string
}

/**
 * A smooth price line over a fading fill of its own colour, with the latest
 * point marked. Same library and styling as the Dashboard's live price chart;
 * sized by its container.
 */
const GradientSparkline = ({ values, color, baseline, label }: GradientSparklineProps) => {
  const chart = useChartTheme()
  const gradientId = `spark-fill-${useId().replace(/:/g, '')}`
  if (values.length < 2) return null

  const data = values.map((value, index) => ({ index, value }))
  const levels = baseline != null ? [...values, baseline] : values
  const low = Math.min(...levels)
  const high = Math.max(...levels)
  const pad = (high - low) * 0.1 || Math.abs(high) * 0.001 || 1
  const last = data.length - 1

  return (
    <div role="img" aria-label={label} className="h-full w-full" data-testid="position-sparkline">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 2, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.38} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis hide domain={[low - pad, high + pad]} />
          {baseline != null && (
            <ReferenceLine y={baseline} stroke={chart.cursor} strokeDasharray="2 3" strokeWidth={1} />
          )}
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.75}
            fill={`url(#${gradientId})`}
            baseValue={low - pad}
            isAnimationActive={false}
            dot={(props: { cx?: number; cy?: number; index?: number }) =>
              props.index === last && props.cx != null && props.cy != null ? (
                <circle key="last" cx={props.cx} cy={props.cy} r={2.75} fill={color} stroke={chart.surface} strokeWidth={1.5} />
              ) : (
                <g key={props.index} />
              )
            }
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default GradientSparkline
